import '@splidejs/react-splide/css';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ReactComponent as SwapIcon } from '../../assets/images/icons/ic-swap.svg';
import { ReactComponent as CheckIcon } from '../../assets/images/icons/ic-check.svg';
import { BlockType } from '../../common/constants';
import { Each } from '../../common/Each';
import useClickOutside from '../../common/hooks/useClickOutside';
import useBeforeUnload from '../../common/hooks/useBeforeUnload';
import usePrompt from '../../common/hooks/usePrompt';
import MainContext from '../../common/MainContext';
import { DialogStatus } from '../../enums';
import AlertDialog from '../dialogs/AlertDialog';
import styles from './PageBuilder.module.css';
import { DEVICE_PRESETS, DEVICE_SAFE_AREAS, DEVICE_SIMULATOR_CONFIG, MOBILE_BREAKPOINT, PREVIEW_MIN_HEIGHT, TABLET_BREAKPOINT, VIEW_PRESETS, viewOptions, VIEWPORT_VERTICAL_OFFSET } from "./PageBuilderConstants";
import PageBuilderToolbar from './PageBuilderToolbar';
import useBlockDialog from './hooks/useBlockDialog';
import PageBlock from './PageBlock';
import { usePageBuilder } from './PageBuilderContext';
import { ReactComponent as SimChevronLeft } from '../../assets/images/icons/simulator/sim-chevron-left.svg'; 
import { ReactComponent as SimChevronRight } from '../../assets/images/icons/simulator/sim-chevron-right.svg'; 
import { ReactComponent as SimReload } from '../../assets/images/icons/simulator/sim-reload.svg'; 
import { ReactComponent as SimReadMode } from '../../assets/images/icons/simulator/sim-read-mode.svg'; 
import { ReactComponent as SimEllipsis } from '../../assets/images/icons/simulator/sim-ellipsis.svg'; 
import { ReactComponent as SimWifi } from '../../assets/images/icons/simulator/sim-wifi.svg'; 
import { ReactComponent as SimBattery } from '../../assets/images/icons/simulator/sim-battery.svg'; 

const VIEWPORT_SNAPSHOT_STORAGE_KEY = 'page-builder-version-switch-viewport-snapshot';
const BUILDER_SHORTCUTS = [
    { keys: 'Click', description: 'Seleziona un singolo blocco' },
    { keys: '⌘/Ctrl + Click', description: 'Seleziona solo il blocco cliccato (single-select)' },
    { keys: '⌘/Ctrl + ⇧ + Click', description: 'Aggiunge il blocco cliccato alla selezione' },
    { keys: '⇧ + Click (sibling)', description: 'Seleziona il range tra sibling dallo anchor corrente' },
    { keys: 'Click fuori builder', description: 'Deseleziona i blocchi selezionati' },
    { keys: '⌘ + \\', description: 'Attiva/disattiva focus mode' },
    { keys: '⌘ + ⇧ + A / S / D', description: 'Cambia vista desktop / tablet / mobile' },
    { keys: '+', description: 'Aggiunge una sezione (o apre il dialog blocchi se c’è una selezione)' },
    { keys: '⌘ + ⌫', description: 'Elimina tutti i blocchi selezionati' },
    { keys: '⌘ + ↑ / ↓ / ← / →', description: 'Sposta il blocco selezionato' },
    { keys: '⌘ + ⇧ + ↑ / ↓ / ← / →', description: 'Aggiunge sibling precedente/successivo alla selezione' },
    { keys: '⇧ + Tab', description: 'Seleziona il sibling successivo' },
    { keys: '⌘ + H', description: 'Nasconde/mostra il blocco nella vista corrente' },
    { keys: '⌘ + ⇧ + I', description: 'Seleziona il blocco interno' },
    { keys: '⌘ + ⇧ + O', description: 'Seleziona il blocco esterno' },
    { keys: '⌘ + ⇧ + C', description: 'Copia blocchi selezionati' },
    { keys: '⌘ + ⇧ + V', description: 'Incolla blocchi selezionati' },
    { keys: '⌘ + ⇧ + X', description: 'Taglia blocchi selezionati' },
    { keys: '⌘ + ⇧ + Z', description: 'Undo' },
];
const PageBuilder = ({
    element,
    customTab,
    onContentChange = () => { },
    onSave = () => { },
    onVersionChange = null,
    isSaving = false,
    lastSavedAt = null,
    publishButtonStatus = null,
    saveDraftButtonStatus = null,
    hideExportToDraft = false
}) => {

    usePrompt('Sei sicuro di voler uscire dal builder?', true)
    useBeforeUnload('Sei sicuro di voler uscire dal builder?', true)

    const context = useContext(MainContext)
    const { sideNavOpen, setSideNavOpen } = context;
    const {
        page,
        publishedPage,
        draftPage,
        hasDraft,
        activeVersion,
        switchVersion,
        selectedBlockId,
        selectedBlockIds,
        selectedBlock,
        selectedBlocks,
        selectBlock,
        deleteBlock,
        deleteBlocks,
        addBlock,
        addBlockTemplate,
        pasteBlock,
        pasteBlocks,
        switchBlockPosition,
        switchBlocksPosition,
        selectChild,
        selectParent,
        selectNextSibling,
        selectPrevSibling,
        toggleHideBlock,
        undo,
        redo
    } = usePageBuilder();
    const [grid, setGrid] = useState(false);
    const [view, setView] = useState('desktop');
    const viewRef = useRef(view);

    const [openToolbar, setOpenToolbar] = useState(true)
    const [selectionHoles, setSelectionHoles] = useState([]);
    const [copiedBlock, _setCopiedBlock] = useState(null);
    const copiedBlockRef = useRef(copiedBlock);
    const setCopiedBlock = (data) => {
        copiedBlockRef.current = data;
        _setCopiedBlock(data);
    }

    const containerRef = useRef(null)
    const containerViewportRef = useRef(null);
    const builderContentRef = useRef(null);
    const viewportCentererRef = useRef(null);
    const builderViewportRef = useRef(null);
    const scaleFactorRef = useRef(1);
    const resizeStateRef = useRef({ active: false, startX: 0, startWidth: 0, pointerId: null, scale: 1 });
    const heightResizeStateRef = useRef({ active: false, startY: 0, startHeight: 0, pointerId: null, scale: 1 });
    const [builderWidth, setBuilderWidth] = useState(null);
    const [builderHeight, setBuilderHeight] = useState(null);
    const [selectedDevicePreset, setSelectedDevicePreset] = useState('custom');
    const [isResizing, setIsResizing] = useState(false);
    const [isResizingHeight, setIsResizingHeight] = useState(false);
    const [containerBounds, setContainerBounds] = useState({ width: 0, height: 0 });
    const [customWidthInput, setCustomWidthInput] = useState('');
    const [customHeightInput, setCustomHeightInput] = useState('');
    const [iosSimulatorMode, setIosSimulatorMode] = useState(true);
    const [isSafariBarOpen, setIsSafariBarOpen] = useState(true);
    const [isPreviewMode, setIsPreviewMode] = useState(false);
    const [scaleMode, setScaleMode] = useState('fit');
    const [viewDropdownOpen, setViewDropdownOpen] = useState(false);
    const [scaleDropdownOpen, setScaleDropdownOpen] = useState(false);
    const [dimensionsDropdownOpen, setDimensionsDropdownOpen] = useState(false);
    const [presetDropdownOpen, setPresetDropdownOpen] = useState(false);
    const [optionsDropdownOpen, setOptionsDropdownOpen] = useState(false);
    const viewDropdownRef = useRef(null);
    const scaleDropdownRef = useRef(null);
    const dimensionsDropdownRef = useRef(null);
    const presetDropdownRef = useRef(null);
    const optionsDropdownRef = useRef(null);
    const safariLastScrollTopRef = useRef(0);
    const safariScrollAccumulatorRef = useRef(0);
    const safariScrollDirectionRef = useRef(0);
    const widthInputActiveRef = useRef(false);
    const heightInputActiveRef = useRef(false);
    const initializedDimensionsRef = useRef(false);
    const measureRafRef = useRef(null);
    const [forcedToolbarTab, setForcedToolbarTab] = useState(null);
    const [isEyedropActive, setIsEyedropActive] = useState(false);
    const [shortcutsOpen, setShortcutsOpen] = useState(false);
    const shortcutsPanelRef = useRef(null);
    const [pendingVersion, setPendingVersion] = useState(null);
    const [showVersionSwitchDialog, setShowVersionSwitchDialog] = useState(false);
    const [showPublishDraftDialog, setShowPublishDraftDialog] = useState(false);
    const [showExportToDraftDialog, setShowExportToDraftDialog] = useState(false);
    const versionSwitchViewportSnapshotRef = useRef(null);
    const shouldRestoreViewportAfterVersionSwitchRef = useRef(false);
    const [isFocusMode, setIsFocusMode] = useState(true);
    const focusPrevSideNavOpenRef = useRef(null);

    const { openBlockDialog, renderBlockDialog } = useBlockDialog((type) => addBlock(selectedBlockId, type), (template) => { addBlockTemplate(selectedBlockId, template.content) });

    useClickOutside(shortcutsPanelRef, () => {
        if (shortcutsOpen) {
            setShortcutsOpen(false);
        }
    });

    const availableScaleFactor = useMemo(() => {
        if (!builderWidth || !builderHeight) {
            return 1;
        }
        if (!containerBounds.width || !containerBounds.height) {
            return 1;
        }
        if (builderWidth <= 0 || builderHeight <= 0) {
            return 1;
        }
        const widthScale = containerBounds.width / builderWidth;
        const heightScale = containerBounds.height / builderHeight;
        const candidate = Math.min(widthScale, heightScale);
        if (!Number.isFinite(candidate) || candidate <= 0) {
            return 1;
        }
        return candidate;
    }, [builderHeight, builderWidth, containerBounds.height, containerBounds.width]);

    const fitScaleFactor = useMemo(() => {
        return Math.min(availableScaleFactor, 1);
    }, [availableScaleFactor]);

    const scaleFactor = useMemo(() => {
        const candidate = scaleMode === 'fill' ? availableScaleFactor : fitScaleFactor;
        if (!Number.isFinite(candidate) || candidate <= 0) {
            return 1;
        }
        return candidate;
    }, [availableScaleFactor, fitScaleFactor, scaleMode]);

    const effectiveScalePercent = useMemo(() => {
        return Math.round(Math.max(scaleFactor * 100, 0));
    }, [scaleFactor]);

    useEffect(() => {
        onContentChange(page, {
            activeVersion,
            publishedPage,
            draftPage
        });
    }, [activeVersion, draftPage, onContentChange, page, publishedPage]);

    const isCornerResizing = isResizing && isResizingHeight;

    const builderViewportStyle = useMemo(() => {
        const style = {
            transformOrigin: 'center center'
        };
        if (builderWidth !== null) {
            style.width = builderWidth;
            style.minWidth = builderWidth;
            style.maxWidth = builderWidth;
            style.flex = '0 0 auto';
        }
        if (builderHeight !== null) {
            style.height = builderHeight;
            style.maxHeight = builderHeight;
        }
        if (scaleFactor !== 1) {
            style.transform = `scale(${scaleFactor})`;
        }
        return style;
    }, [builderHeight, builderWidth, scaleFactor]);

    const viewportSize = useMemo(() => {
        const width = Number.isFinite(builderWidth) ? builderWidth : containerBounds.width;
        const height = Number.isFinite(builderHeight) ? builderHeight : containerBounds.height;
        return {
            width: Number.isFinite(width) ? width : null,
            height: Number.isFinite(height) ? height : null
        };
    }, [builderHeight, builderWidth, containerBounds.height, containerBounds.width]);

    const selectedDevicePresetConfig = useMemo(() => {
        if (!selectedDevicePreset || selectedDevicePreset === 'custom') {
            return null;
        }
        return DEVICE_PRESETS.find((preset) => preset.id === selectedDevicePreset) ?? null;
    }, [selectedDevicePreset]);

    const isIphonePreset = useMemo(() => {
        return Boolean(selectedDevicePresetConfig?.id?.startsWith('iphone-'));
    }, [selectedDevicePresetConfig]);

    const builderContainerStyle = useMemo(() => {
        return {
            '--builder-font-scale': `${selectedDevicePresetConfig?.fontScale ?? 1}`
        };
    }, [selectedDevicePresetConfig]);

    const handleBuilderMouseDownCapture = useCallback((event) => {
        if (event.shiftKey || event.metaKey || event.ctrlKey) {
            event.preventDefault();
        }
    }, []);

    const selectedPresetSafeArea = useMemo(() => {
        if (!selectedDevicePreset || selectedDevicePreset === 'custom') {
            return null;
        }
        return DEVICE_SAFE_AREAS[selectedDevicePreset] ?? null;
    }, [selectedDevicePreset]);

    const simulatorDeviceConfig = useMemo(() => {
        if (!selectedDevicePreset || selectedDevicePreset === 'custom') {
            return null;
        }
        return DEVICE_SIMULATOR_CONFIG[selectedDevicePreset] ?? null;
    }, [selectedDevicePreset]);

    const isIosSimulatorActive = iosSimulatorMode && Boolean(selectedPresetSafeArea);
    const hasDynamicIsland = isIosSimulatorActive && Boolean(simulatorDeviceConfig?.hasDynamicIsland);
    const hasNotch = isIosSimulatorActive && Boolean(simulatorDeviceConfig?.hasNotch);
    const hasTopCutout = hasDynamicIsland || hasNotch;

    const simulatorSafeBottom = useMemo(() => {
        if (!selectedPresetSafeArea) {
            return 0;
        }

        if (typeof selectedPresetSafeArea.bottom === 'number') {
            return selectedPresetSafeArea.bottom;
        }

        const openValue = selectedPresetSafeArea.bottomOpen;
        const closedValue = selectedPresetSafeArea.bottomClosed;

        if (isSafariBarOpen && Number.isFinite(openValue)) {
            return openValue;
        }

        if (!isSafariBarOpen && Number.isFinite(closedValue)) {
            return closedValue;
        }

        return 0;
    }, [isSafariBarOpen, selectedPresetSafeArea]);

    useEffect(() => {
        safariLastScrollTopRef.current = 0;
        safariScrollAccumulatorRef.current = 0;
        safariScrollDirectionRef.current = 0;
        setIsSafariBarOpen(true);
    }, [isIosSimulatorActive, selectedDevicePreset]);

    const simulatorInsetStyle = useMemo(() => {
        if (!isIosSimulatorActive || !selectedPresetSafeArea) {
            return undefined;
        }

        return {
            '--sim-safe-top': `${selectedPresetSafeArea.top ?? 0}px`,
            '--sim-safe-right': `${selectedPresetSafeArea.right ?? 0}px`,
            '--sim-safe-bottom': `${simulatorSafeBottom}px`,
            '--sim-safe-left': `${selectedPresetSafeArea.left ?? 0}px`,
            '--sim-status-top': `${simulatorDeviceConfig?.statusBarTop ?? 26}px`,
            '--sim-status-side': `${simulatorDeviceConfig?.statusBarSide ?? 40}px`,
            '--sim-time-font-size': `${simulatorDeviceConfig?.timeFontSize ?? 14}px`,
            '--sim-status-icons-scale': `${simulatorDeviceConfig?.statusIconsScale ?? 1}`,
            '--sim-island-top': `${simulatorDeviceConfig?.islandTop ?? 26}px`,
            '--sim-island-width': `${simulatorDeviceConfig?.islandWidth ?? 126}px`,
            '--sim-island-height': `${simulatorDeviceConfig?.islandHeight ?? 30}px`
        };
    }, [isIosSimulatorActive, selectedPresetSafeArea, simulatorDeviceConfig, simulatorSafeBottom]);

    const measureSelectionHole = useCallback(() => {
        const runMeasurement = () => {
            const containerEl = containerRef.current;
            const viewportEl = containerViewportRef.current;
            if (!containerEl || !viewportEl) {
                setSelectionHoles([]);
                return;
            }

            const selectedIds = Array.isArray(selectedBlockIds) && selectedBlockIds.length > 0
                ? selectedBlockIds
                : (selectedBlock?.id ? [selectedBlock.id] : []);

            if (selectedIds.length === 0) {
                setSelectionHoles([]);
                return;
            }

            const viewportRect = viewportEl.getBoundingClientRect();
            const scale = scaleFactorRef.current || 1;

            const holes = selectedIds.map((id) => {
                const blockElement = document.getElementById(id);
                if (!blockElement) {
                    return null;
                }
                const blockRect = blockElement.getBoundingClientRect();
                return {
                    id,
                    top: blockRect.top - viewportRect.top,
                    left: blockRect.left - viewportRect.left,
                    width: blockRect.width,
                    height: blockRect.height,
                    viewportWidth: viewportRect.width,
                    viewportHeight: viewportRect.height,
                    scale
                };
            }).filter(Boolean);

            setSelectionHoles(holes);
        };

        if (typeof window === 'undefined') {
            runMeasurement();
            return;
        }

        if (measureRafRef.current) {
            cancelAnimationFrame(measureRafRef.current);
        }

        measureRafRef.current = window.requestAnimationFrame(() => {
            runMeasurement();
            measureRafRef.current = null;
        });
    }, [selectedBlock?.id, selectedBlockIds]);

    const clampBuilderWidth = useCallback((nextWidth) => {
        const fallback = VIEW_PRESETS.desktop.width;
        const safeWidth = Number.isFinite(nextWidth) ? nextWidth : fallback;
        return Math.max(safeWidth, 0);
    }, []);

    const determineViewFromWidth = useCallback((width) => {
        if (!Number.isFinite(width)) {
            return 'desktop';
        }
        if (width <= MOBILE_BREAKPOINT) {
            return 'mobile';
        }
        if (width <= TABLET_BREAKPOINT) {
            return 'tablet';
        }
        return 'desktop';
    }, []);

    const clampBuilderHeight = useCallback((nextHeight) => {
        const safeHeight = Number.isFinite(nextHeight) ? nextHeight : PREVIEW_MIN_HEIGHT;
        return Math.max(safeHeight, PREVIEW_MIN_HEIGHT);
    }, []);

    const applyViewPreset = useCallback((mode) => {
        if (!mode || !VIEW_PRESETS[mode]) {
            return;
        }
        const preset = VIEW_PRESETS[mode];
        setView(mode);
        setBuilderWidth(preset.width);
        setBuilderHeight(preset.height);
    }, []);

    const toggleFocusMode = useCallback(() => {
        setIsFocusMode((previous) => {
            const next = !previous;

            if (next) {
                focusPrevSideNavOpenRef.current = Boolean(sideNavOpen);
                if (typeof setSideNavOpen === 'function') {
                    setSideNavOpen(false);
                }
                setOpenToolbar(true);
            } else {
                if (focusPrevSideNavOpenRef.current !== null && typeof setSideNavOpen === 'function') {
                    setSideNavOpen(Boolean(focusPrevSideNavOpenRef.current));
                }
                focusPrevSideNavOpenRef.current = null;
            }

            return next;
        });
    }, [setSideNavOpen, sideNavOpen]);

    useEffect(() => {
        if (!isFocusMode) {
            return;
        }
        if (focusPrevSideNavOpenRef.current === null) {
            focusPrevSideNavOpenRef.current = Boolean(sideNavOpen);
        }
        if (sideNavOpen && typeof setSideNavOpen === 'function') {
            setSideNavOpen(false);
        }
    }, [isFocusMode, setSideNavOpen, sideNavOpen]);

    useEffect(() => {
        // Restore the menu itself if an earlier focus-mode implementation hid it.
        // Visibility now belongs to the rail, so both elements return together.
        const menu = document.getElementById('sidenav');
        if (menu && ('focusModePrevDisplay' in menu.dataset || (document.getElementById('sidenav-rail') && menu.style.display === 'none'))) {
            menu.style.display = menu.dataset.focusModePrevDisplay || '';
            delete menu.dataset.focusModePrevDisplay;
        }
        if (!isFocusMode) return;
        const sideNavElement = document.getElementById('sidenav-rail') || menu;
        if (!sideNavElement) return;
        const previousDisplay = sideNavElement.style.display;
        sideNavElement.style.display = 'none';
        return () => {
            sideNavElement.style.display = previousDisplay;
        };
    }, [isFocusMode]);

    useEffect(() => {

        // const toggleHideBlock = () => {
        //     if (!selectedBlockRef.current) {
        //         return;
        //     }

        //     const styleKey = viewRef.current === 'tablet'
        //         ? 'styleTablet'
        //         : viewRef.current === 'mobile'
        //             ? 'styleMobile'
        //             : 'style';

        //     const updateBlockStyleInContent = (content) => {
        //         return content.map(item => {
        //             if (item.id === selectedBlockRef.current.id) {
        //                 const targetStyle = { ...(item[styleKey] ?? {}) };
        //                 const isHidden = targetStyle.display === 'none';

        //                 return {
        //                     ...item,
        //                     [styleKey]: {
        //                         ...targetStyle,
        //                         display: isHidden ? 'flex' : 'none'
        //                     }
        //                 };
        //             }
        //             if (item.content) {
        //                 return {
        //                     ...item,
        //                     content: updateBlockStyleInContent(item.content)
        //                 };
        //             }
        //             return item;
        //         });
        //     };

        //     setPage(prevPage => {
        //         const nextPage = updateBlockStyleInContent(prevPage);
        //         const updatedSelection = findBlockById(selectedBlockRef.current.id, nextPage);
        //         if (updatedSelection) {
        //             setSelectedBlock(updatedSelection);
        //         }
        //         return nextPage;
        //     });
        // }



        if (typeof ResizeObserver === 'undefined') {
            return;
        }
        const target = viewportCentererRef.current;
        if (!target) {
            return;
        }

        const computeBounds = () => {
            const containerEl = viewportCentererRef.current;
            if (!containerEl) {
                return;
            }
            const rect = containerEl.getBoundingClientRect();
            const width = rect.width;
            const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : rect.height;
            let availableHeight = viewportHeight - rect.top - VIEWPORT_VERTICAL_OFFSET;
            if (!Number.isFinite(availableHeight)) {
                availableHeight = rect.height;
            }
            availableHeight = Math.max(availableHeight, 1);
            setContainerBounds({ width, height: availableHeight });
        };

        const observer = new ResizeObserver(() => computeBounds());
        observer.observe(target);
        computeBounds();

        if (typeof window !== 'undefined') {
            window.addEventListener('resize', computeBounds);
            window.addEventListener('scroll', computeBounds, { passive: true });
        }

        return () => {
            if (measureRafRef.current && typeof window !== 'undefined') {
                cancelAnimationFrame(measureRafRef.current);
            }
            observer.disconnect();
            if (typeof window !== 'undefined') {
                window.removeEventListener('resize', computeBounds);
                window.removeEventListener('scroll', computeBounds);
            }
        };
    }, []);

    useEffect(() => {

        const handleKeyDown = async (event) => {
            const selectedIds = Array.isArray(selectedBlockIds) ? selectedBlockIds.filter(Boolean) : [];
            const selectedBlocksList = Array.isArray(selectedBlocks)
                ? selectedBlocks.filter(Boolean)
                : [];
            const multiSelectedBlocks = selectedIds.length > 1
                ? selectedIds
                    .map((id) => selectedBlocksList.find((blockItem) => blockItem?.id === id))
                    .filter(Boolean)
                : [];
            const blocksToDelete = selectedIds.length > 0
                ? [...selectedIds]
                : (selectedBlockId ? [selectedBlockId] : []);

            const viewShortcuts = {
                a: 'desktop',
                s: 'tablet',
                d: 'mobile'
            };

            const isFocusModeShortcut = event.metaKey
                && !event.ctrlKey
                && (event.code === 'Backslash' || event.key === '\\' || event.key === '|');

            if (isFocusModeShortcut) {
                event.preventDefault();
                toggleFocusMode();
                return;
            }

            if (event.metaKey && event.shiftKey) {
                const targetView = viewShortcuts[event.key?.toLowerCase?.()];
                if (targetView) {
                    event.preventDefault();
                    applyViewPreset(targetView);
                    return;
                }
            }

            if (['Delete', 'Backspace'].includes(event.key) && (event.metaKey || event.ctrlKey)) {
                event.preventDefault();
                if (blocksToDelete.length > 1) {
                    deleteBlocks(blocksToDelete);
                }
                else {
                    [...blocksToDelete].reverse().forEach((id) => {
                        deleteBlock(id);
                    });
                }
            }
            if (['+'].includes(event.key)) {
                if (!selectedBlockId) {
                    addBlock(null, BlockType.Section, page);
                }
                else {
                    openBlockDialog()
                }
            }
            if (['ArrowUp', 'ArrowLeft'].includes(event.key) && (event.metaKey || event.ctrlKey) && event.shiftKey) {
                event.preventDefault();
                selectPrevSibling(selectedBlockId, { addOnly: true })
            }
            if (['ArrowDown', 'ArrowRight'].includes(event.key) && (event.metaKey || event.ctrlKey) && event.shiftKey) {
                event.preventDefault();
                selectNextSibling(selectedBlockId, { addOnly: true })
            }
            if (['ArrowUp', 'ArrowLeft'].includes(event.key) && (event.metaKey || event.ctrlKey) && !event.shiftKey) {
                event.preventDefault();
                if (selectedIds.length > 1) {
                    switchBlocksPosition(selectedIds, 'up')
                }
                else {
                    switchBlockPosition(selectedBlockId, 'up')
                }
            }
            if (['ArrowDown', 'ArrowRight'].includes(event.key) && (event.metaKey || event.ctrlKey) && !event.shiftKey) {
                event.preventDefault();
                if (selectedIds.length > 1) {
                    switchBlocksPosition(selectedIds, 'down')
                }
                else {
                    switchBlockPosition(selectedBlockId, 'down')
                }
            }
            if (['Tab'].includes(event.key) && event.shiftKey) {
                event.preventDefault();
                selectNextSibling(selectedBlockId)
            }
            if (['h'].includes(event.key) && (event.metaKey || event.ctrlKey)) {
                //Setto il display none al blocco
                event.preventDefault();
                toggleHideBlock(selectedBlockId, viewRef.current);
            }
            if (['i'].includes(event.key) && (event.metaKey || event.ctrlKey) && event.shiftKey) {
                event.preventDefault();
                const child = selectChild(selectedBlockId)
                if (child?.id) {
                    selectBlock(child.id)
                }
            }
            if (['o'].includes(event.key) && (event.metaKey || event.ctrlKey) && event.shiftKey) {
                event.preventDefault();
                const parent = selectParent(selectedBlockId)
                if (parent?.id) {
                    selectBlock(parent.id)
                }
            }
            if (['c'].includes(event.key) && (event.metaKey || event.ctrlKey) && event.shiftKey) {
                event.preventDefault();
                if (multiSelectedBlocks.length > 0) {
                    await navigator.clipboard.writeText(JSON.stringify({
                        type: 'velv-builder-blocks',
                        blocks: multiSelectedBlocks
                    }));
                    return;
                }
                if (selectedBlock) {
                    await navigator.clipboard.writeText(JSON.stringify(selectedBlock));
                }
            }
            if (['v'].includes(event.key) && (event.metaKey || event.ctrlKey) && event.shiftKey) {
                event.preventDefault();
                try {
                    let copiedBlockText = await navigator.clipboard.readText();
                    let copiedPayload = JSON.parse(copiedBlockText);

                    if (!selectedBlockId) {
                        throw new Error('No target block selected')
                    }

                    if (copiedPayload?.type === 'velv-builder-blocks' && Array.isArray(copiedPayload.blocks)) {
                        pasteBlocks(selectedBlockId, copiedPayload.blocks);
                        return;
                    }

                    if (Array.isArray(copiedPayload)) {
                        pasteBlocks(selectedBlockId, copiedPayload);
                        return;
                    }

                    if (!(copiedPayload?.id)) {
                        throw new Error('Invalid block')
                    }

                    pasteBlock(selectedBlockId, copiedPayload);
                }
                catch (e) {
                    console.error('Error while pasting', e)
                }
            }
            if (['x'].includes(event.key) && (event.metaKey || event.ctrlKey) && event.shiftKey) {
                event.preventDefault();
                if (multiSelectedBlocks.length > 0) {
                    await navigator.clipboard.writeText(JSON.stringify({
                        type: 'velv-builder-blocks',
                        blocks: multiSelectedBlocks
                    }));
                    deleteBlocks(blocksToDelete);
                    return;
                }

                if (selectedBlock) {
                    await navigator.clipboard.writeText(JSON.stringify(selectedBlock));
                    [...blocksToDelete].reverse().forEach((id) => {
                        deleteBlock(id);
                    });
                }
            }
            // if (['z'].includes(event.key) && (event.metaKey || event.ctrlKey)) {
            //     event.preventDefault();
            //     undo();
            // }
            if (['z'].includes(event.key) && (event.metaKey || event.ctrlKey) && event.shiftKey) {
                event.preventDefault();
                undo();
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);

        }
    }, [
        selectedBlockId,
        selectedBlockIds,
        selectedBlock,
        selectedBlocks,
        page,
        applyViewPreset,
        deleteBlock,
        deleteBlocks,
        addBlock,
        openBlockDialog,
        switchBlockPosition,
        selectNextSibling,
        selectPrevSibling,
        selectChild,
        selectParent,
        selectBlock,
        pasteBlock,
        pasteBlocks,
        toggleHideBlock,
        undo,
        switchBlocksPosition,
        redo,
        toggleFocusMode
    ]);

    useEffect(() => {
        viewRef.current = view;
    }, [view]);

    useEffect(() => {
        if (!scaleDropdownOpen) {
            return;
        }

        const handlePointerDown = (event) => {
            if (!scaleDropdownRef.current) {
                return;
            }
            if (scaleDropdownRef.current.contains(event.target)) {
                return;
            }
            setScaleDropdownOpen(false);
        };

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                setScaleDropdownOpen(false);
            }
        };

        document.addEventListener('pointerdown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('pointerdown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [scaleDropdownOpen]);

    useEffect(() => {
        if (!dimensionsDropdownOpen) {
            return;
        }

        const handlePointerDown = (event) => {
            if (!dimensionsDropdownRef.current) {
                return;
            }
            if (dimensionsDropdownRef.current.contains(event.target)) {
                return;
            }
            setDimensionsDropdownOpen(false);
        };

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                setDimensionsDropdownOpen(false);
            }
        };

        document.addEventListener('pointerdown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('pointerdown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [dimensionsDropdownOpen]);

    useEffect(() => {
        if (!viewDropdownOpen) {
            return;
        }

        const handlePointerDown = (event) => {
            if (!viewDropdownRef.current) {
                return;
            }
            if (viewDropdownRef.current.contains(event.target)) {
                return;
            }
            setViewDropdownOpen(false);
        };

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                setViewDropdownOpen(false);
            }
        };

        document.addEventListener('pointerdown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('pointerdown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [viewDropdownOpen]);

    useEffect(() => {
        if (!presetDropdownOpen) {
            return;
        }

        const handlePointerDown = (event) => {
            if (!presetDropdownRef.current) {
                return;
            }
            if (presetDropdownRef.current.contains(event.target)) {
                return;
            }
            setPresetDropdownOpen(false);
        };

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                setPresetDropdownOpen(false);
            }
        };

        document.addEventListener('pointerdown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('pointerdown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [presetDropdownOpen]);

    useEffect(() => {
        if (!optionsDropdownOpen) {
            return;
        }

        const handlePointerDown = (event) => {
            if (!optionsDropdownRef.current) {
                return;
            }
            if (optionsDropdownRef.current.contains(event.target)) {
                return;
            }
            setOptionsDropdownOpen(false);
        };

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                setOptionsDropdownOpen(false);
            }
        };

        document.addEventListener('pointerdown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('pointerdown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [optionsDropdownOpen]);

    useEffect(() => {
        if (!isIphonePreset && optionsDropdownOpen) {
            setOptionsDropdownOpen(false);
        }
    }, [isIphonePreset, optionsDropdownOpen]);

    useEffect(() => {
        if (selectedDevicePreset !== 'custom' && dimensionsDropdownOpen) {
            setDimensionsDropdownOpen(false);
        }
    }, [dimensionsDropdownOpen, selectedDevicePreset]);

    useEffect(() => {
        if (!isPreviewMode) {
            return;
        }
        if (!selectedBlockId) {
            return;
        }
        selectBlock(null);
    }, [isPreviewMode, selectedBlockId, selectBlock]);

    useEffect(() => {
        if (!isPreviewMode) {
            return;
        }

        const container = containerRef.current;
        if (!container) {
            return;
        }

        const syncPlayback = (videoEl) => {
            const shouldPlay = videoEl.getAttribute('data-playing') === 'true';
            if (shouldPlay) {
                const playPromise = videoEl.play();
                if (playPromise?.catch) {
                    playPromise.catch(() => { });
                }
                return;
            }

            videoEl.pause();
        };

        const videoBlocks = container.querySelectorAll('video');
        const observers = [];

        videoBlocks.forEach((videoEl) => {
            syncPlayback(videoEl);

            const observer = new MutationObserver((mutations) => {
                for (const mutation of mutations) {
                    if (mutation.type === 'attributes' && mutation.attributeName === 'data-playing') {
                        syncPlayback(videoEl);
                    }
                }
            });

            observer.observe(videoEl, { attributes: true, attributeFilter: ['data-playing'] });
            observers.push(observer);
        });

        return () => {
            observers.forEach((observer) => observer.disconnect());
        };
    }, [isPreviewMode, page]);

    useEffect(() => {
        if (widthInputActiveRef.current) {
            return;
        }
        setCustomWidthInput(builderWidth ? Math.round(builderWidth).toString() : '');
    }, [builderWidth]);

    useEffect(() => {
        if (heightInputActiveRef.current) {
            return;
        }
        setCustomHeightInput(builderHeight ? Math.round(builderHeight).toString() : '');
    }, [builderHeight]);

    useEffect(() => {
        if (!shouldRestoreViewportAfterVersionSwitchRef.current) {
            return;
        }

        const snapshot = versionSwitchViewportSnapshotRef.current;
        if (!snapshot) {
            shouldRestoreViewportAfterVersionSwitchRef.current = false;
            return;
        }

        if (Number.isFinite(snapshot.builderWidth)) {
            setBuilderWidth(snapshot.builderWidth);
        }
        if (Number.isFinite(snapshot.builderHeight)) {
            setBuilderHeight(snapshot.builderHeight);
        }
        if (typeof snapshot.customWidthInput === 'string') {
            setCustomWidthInput(snapshot.customWidthInput);
        }
        if (typeof snapshot.customHeightInput === 'string') {
            setCustomHeightInput(snapshot.customHeightInput);
        }
        if (typeof snapshot.selectedDevicePreset === 'string') {
            setSelectedDevicePreset(snapshot.selectedDevicePreset);
        }
        if (typeof snapshot.view === 'string') {
            setView(snapshot.view);
        }
        if (snapshot.scaleMode === 'fit' || snapshot.scaleMode === 'fill') {
            setScaleMode(snapshot.scaleMode);
        }
        else if (snapshot.scaleMode === 'fixed') {
            setScaleMode('fill');
        }
        else if (typeof snapshot.scaleIsForced === 'boolean') {
            setScaleMode(snapshot.scaleIsForced ? 'fill' : 'fit');
        }

        shouldRestoreViewportAfterVersionSwitchRef.current = false;
    }, [activeVersion]);

    useEffect(() => {
        if (builderWidth === null || builderHeight === null) {
            if (selectedDevicePreset !== 'custom') {
                setSelectedDevicePreset('custom');
            }
            return;
        }
        const roundedWidth = Math.round(builderWidth);
        const roundedHeight = Math.round(builderHeight);
        const match = DEVICE_PRESETS.find((preset) => {
            return Math.round(preset.width) === roundedWidth && Math.round(preset.height) === roundedHeight;
        });
        const nextPreset = match ? match.id : 'custom';
        if (nextPreset !== selectedDevicePreset) {
            setSelectedDevicePreset(nextPreset);
        }
    }, [builderWidth, builderHeight, selectedDevicePreset]);

    useEffect(() => {
        onContentChange(page, {
            activeVersion,
            publishedPage,
            draftPage
        });
    }, [activeVersion, draftPage, onContentChange, page, publishedPage]);

    useEffect(() => {
        measureSelectionHole();
    }, [selectedBlockId, page, view, grid, builderWidth, builderHeight, containerBounds, measureSelectionHole]);

    useEffect(() => {
        const handleResize = () => measureSelectionHole();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [measureSelectionHole]);

    useEffect(() => {
        const containerEl = containerRef.current;
        if (!containerEl) {
            return;
        }
        safariLastScrollTopRef.current = containerEl.scrollTop;

        const handleScroll = () => {
            measureSelectionHole();

            if (!isIosSimulatorActive || !selectedPresetSafeArea) {
                return;
            }

            const hasOpenClosedSafeArea = Number.isFinite(selectedPresetSafeArea.bottomOpen)
                && Number.isFinite(selectedPresetSafeArea.bottomClosed);

            if (!hasOpenClosedSafeArea) {
                return;
            }

            const currentScrollTop = containerEl.scrollTop;
            const delta = currentScrollTop - safariLastScrollTopRef.current;
            safariLastScrollTopRef.current = currentScrollTop;

            if (Math.abs(delta) < 1) {
                return;
            }

            const direction = delta > 0 ? 1 : -1;

            if (safariScrollDirectionRef.current !== direction) {
                safariScrollDirectionRef.current = direction;
                safariScrollAccumulatorRef.current = 0;
            }

            safariScrollAccumulatorRef.current += Math.abs(delta);

            if (safariScrollAccumulatorRef.current < 20) {
                return;
            }

            safariScrollAccumulatorRef.current = 0;

            if (direction > 0) {
                setIsSafariBarOpen(false);
            } else {
                setIsSafariBarOpen(true);
            }
        };

        containerEl.addEventListener('scroll', handleScroll, { passive: true });
        return () => containerEl.removeEventListener('scroll', handleScroll);
    }, [isIosSimulatorActive, measureSelectionHole, selectedPresetSafeArea]);

    useEffect(() => {
        if (initializedDimensionsRef.current) {
            return;
        }

        try {
            const serializedSnapshot = window.sessionStorage.getItem(VIEWPORT_SNAPSHOT_STORAGE_KEY);
            if (serializedSnapshot) {
                const snapshot = JSON.parse(serializedSnapshot);

                if (Number.isFinite(snapshot?.builderWidth)) {
                    setBuilderWidth(snapshot.builderWidth);
                }
                if (Number.isFinite(snapshot?.builderHeight)) {
                    setBuilderHeight(snapshot.builderHeight);
                }
                if (typeof snapshot?.customWidthInput === 'string') {
                    setCustomWidthInput(snapshot.customWidthInput);
                }
                if (typeof snapshot?.customHeightInput === 'string') {
                    setCustomHeightInput(snapshot.customHeightInput);
                }
                if (typeof snapshot?.selectedDevicePreset === 'string') {
                    setSelectedDevicePreset(snapshot.selectedDevicePreset);
                }
                if (typeof snapshot?.view === 'string') {
                    setView(snapshot.view);
                }
                if (snapshot?.scaleMode === 'fit' || snapshot?.scaleMode === 'fill') {
                    setScaleMode(snapshot.scaleMode);
                }
                else if (snapshot?.scaleMode === 'fixed') {
                    setScaleMode('fill');
                }
                else if (typeof snapshot?.scaleIsForced === 'boolean') {
                    setScaleMode(snapshot.scaleIsForced ? 'fill' : 'fit');
                }

                window.sessionStorage.removeItem(VIEWPORT_SNAPSHOT_STORAGE_KEY);
                initializedDimensionsRef.current = true;
                return;
            }
        } catch (error) {
            window.sessionStorage.removeItem(VIEWPORT_SNAPSHOT_STORAGE_KEY);
        }

        applyViewPreset('desktop');
        initializedDimensionsRef.current = true;
    }, [applyViewPreset]);

    useEffect(() => {
        scaleFactorRef.current = scaleFactor || 1;
    }, [scaleFactor]);

    useEffect(() => {
        measureSelectionHole();
    }, [scaleFactor, measureSelectionHole]);

    useEffect(() => {
        const handlePointerMove = (event) => {
            const horizontalState = resizeStateRef.current;
            if (horizontalState.active && (horizontalState.pointerId === null || event.pointerId === horizontalState.pointerId)) {
                const scale = horizontalState.scale && horizontalState.scale > 0 ? horizontalState.scale : 1;
                const deltaX = (event.clientX - horizontalState.startX) / scale;
                const clampedWidth = clampBuilderWidth(horizontalState.startWidth + deltaX);
                if (clampedWidth !== null) {
                    setBuilderWidth(clampedWidth);
                }
            }

            const verticalState = heightResizeStateRef.current;
            if (verticalState.active && (verticalState.pointerId === null || event.pointerId === verticalState.pointerId)) {
                const scale = verticalState.scale && verticalState.scale > 0 ? verticalState.scale : 1;
                const deltaY = (event.clientY - verticalState.startY) / scale;
                const clampedHeight = clampBuilderHeight(verticalState.startHeight + deltaY);
                if (clampedHeight !== null) {
                    setBuilderHeight(clampedHeight);
                }
            }
        };

        const stopResize = (event) => {
            const horizontalState = resizeStateRef.current;
            if (horizontalState.active && (horizontalState.pointerId === null || event?.pointerId === horizontalState.pointerId)) {
                resizeStateRef.current = { active: false, startX: 0, startWidth: 0, pointerId: null, scale: 1 };
                setIsResizing(false);
            }

            const verticalState = heightResizeStateRef.current;
            if (verticalState.active && (verticalState.pointerId === null || event?.pointerId === verticalState.pointerId)) {
                heightResizeStateRef.current = { active: false, startY: 0, startHeight: 0, pointerId: null, scale: 1 };
                setIsResizingHeight(false);
            }

            if (!resizeStateRef.current.active && !heightResizeStateRef.current.active) {
                document.body.style.userSelect = '';
                document.body.style.cursor = '';
            }
        };

        window.addEventListener('pointermove', handlePointerMove);
        window.addEventListener('pointerup', stopResize);
        window.addEventListener('pointercancel', stopResize);

        return () => {
            window.removeEventListener('pointermove', handlePointerMove);
            window.removeEventListener('pointerup', stopResize);
            window.removeEventListener('pointercancel', stopResize);
        };
    }, [clampBuilderWidth, clampBuilderHeight]);

    useEffect(() => {
        const handleWindowResize = () => {
            setBuilderWidth((prev) => {
                if (prev === null) {
                    return prev;
                }
                const clamped = clampBuilderWidth(prev);
                return clamped ?? prev;
            });
            setBuilderHeight((prev) => {
                if (prev === null) {
                    return prev;
                }
                const clamped = clampBuilderHeight(prev);
                return clamped ?? prev;
            });
        };

        window.addEventListener('resize', handleWindowResize);
        return () => window.removeEventListener('resize', handleWindowResize);
    }, [clampBuilderWidth, clampBuilderHeight]);

    useEffect(() => {
        setBuilderWidth((prev) => {
            if (prev === null) {
                return prev;
            }
            const clamped = clampBuilderWidth(prev);
            return clamped ?? prev;
        });
    }, [openToolbar, clampBuilderWidth]);

    useEffect(() => {
        if (builderWidth === null) {
            return;
        }
        const nextView = determineViewFromWidth(builderWidth);
        setView((prev) => (prev === nextView ? prev : nextView));
    }, [builderWidth, determineViewFromWidth]);

    const startResize = useCallback((event) => {
        if (!builderViewportRef.current) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        const rect = builderViewportRef.current.getBoundingClientRect();
        const scale = scaleFactorRef.current > 0 ? scaleFactorRef.current : 1;
        const unscaledWidth = builderWidth ?? (rect.width / scale);
        resizeStateRef.current = {
            active: true,
            startX: event.clientX,
            startWidth: unscaledWidth,
            pointerId: event.pointerId ?? null,
            scale
        };
        setIsResizing(true);
        document.body.style.userSelect = 'none';
        document.body.style.cursor = 'col-resize';
    }, [builderWidth]);

    const startHeightResize = useCallback((event) => {
        if (!builderViewportRef.current) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        const rect = builderViewportRef.current.getBoundingClientRect();
        const scale = scaleFactorRef.current > 0 ? scaleFactorRef.current : 1;
        const unscaledHeight = builderHeight ?? (rect.height / scale);
        heightResizeStateRef.current = {
            active: true,
            startY: event.clientY,
            startHeight: unscaledHeight,
            pointerId: event.pointerId ?? null,
            scale
        };
        setIsResizingHeight(true);
        document.body.style.userSelect = 'none';
        document.body.style.cursor = 'row-resize';
    }, [builderHeight]);

    const startCornerResize = useCallback((event) => {
        if (!builderViewportRef.current) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        const rect = builderViewportRef.current.getBoundingClientRect();
        const pointerId = event.pointerId ?? null;
        const scale = scaleFactorRef.current > 0 ? scaleFactorRef.current : 1;
        const unscaledWidth = builderWidth ?? (rect.width / scale);
        const unscaledHeight = builderHeight ?? (rect.height / scale);
        resizeStateRef.current = {
            active: true,
            startX: event.clientX,
            startWidth: unscaledWidth,
            pointerId,
            scale
        };
        heightResizeStateRef.current = {
            active: true,
            startY: event.clientY,
            startHeight: unscaledHeight,
            pointerId,
            scale
        };
        setIsResizing(true);
        setIsResizingHeight(true);
        document.body.style.userSelect = 'none';
        document.body.style.cursor = 'nwse-resize';
    }, [builderHeight, builderWidth]);


    const handlePresetClick = useCallback((mode) => {
        applyViewPreset(mode);
    }, [applyViewPreset]);

    const requestToolbarTab = useCallback((tab) => {
        if (!tab) {
            return;
        }
        setForcedToolbarTab(tab);
    }, []);

    const handleForcedTabApplied = useCallback(() => {
        setForcedToolbarTab(null);
    }, []);

    const handleEyedropStateChange = useCallback((active) => {
        setIsEyedropActive(Boolean(active));
    }, []);

    const handleViewportBackgroundMouseDown = useCallback((event) => {
        if (isPreviewMode) {
            return;
        }

        if (!builderViewportRef.current) {
            return;
        }

        if (!builderViewportRef.current.contains(event.target)) {
            selectBlock(null);
        }
    }, [isPreviewMode, selectBlock]);

    const handleVersionRequest = useCallback((version) => {
        if (version === activeVersion) {
            return;
        }

        versionSwitchViewportSnapshotRef.current = {
            builderWidth,
            builderHeight,
            customWidthInput,
            customHeightInput,
            selectedDevicePreset,
            view,
            scaleMode
        };

        setPendingVersion(version);
        setShowVersionSwitchDialog(true);
    }, [activeVersion, builderHeight, builderWidth, customHeightInput, customWidthInput, scaleMode, selectedDevicePreset, view]);

    const applyVersionChange = useCallback(() => {
        if (!pendingVersion) {
            return;
        }

        shouldRestoreViewportAfterVersionSwitchRef.current = true;

        try {
            const serializedSnapshot = JSON.stringify(versionSwitchViewportSnapshotRef.current);
            window.sessionStorage.setItem(VIEWPORT_SNAPSHOT_STORAGE_KEY, serializedSnapshot);
        } catch (error) {
            window.sessionStorage.removeItem(VIEWPORT_SNAPSHOT_STORAGE_KEY);
        }

        if (typeof onVersionChange === 'function') {
            onVersionChange(pendingVersion, {
                activeVersion,
                publishedPage,
                draftPage
            });
        } else {
            switchVersion(pendingVersion);
        }

        setShowVersionSwitchDialog(false);
        setPendingVersion(null);
    }, [activeVersion, draftPage, onVersionChange, pendingVersion, publishedPage, switchVersion]);

    const handlePublishRequest = useCallback(() => {
        if (activeVersion === 'draft') {
            setShowPublishDraftDialog(true);
            return;
        }

        onSave(page, activeVersion, 'publish', {
            publishedPage,
            draftPage
        });
    }, [activeVersion, draftPage, onSave, page, publishedPage]);

    const handleSaveDraftRequest = useCallback(() => {
        onSave(page, activeVersion, 'save_draft', {
            publishedPage,
            draftPage
        });
    }, [activeVersion, draftPage, onSave, page, publishedPage]);

    const handleExportToDraftRequest = useCallback(() => {
        if (activeVersion !== 'published') {
            return;
        }

        setShowExportToDraftDialog(true);
    }, [activeVersion]);

    const handleConfirmPublishDraft = useCallback(() => {
        onSave(page, activeVersion, 'publish', {
            publishedPage,
            draftPage
        });
        setShowPublishDraftDialog(false);
    }, [activeVersion, draftPage, onSave, page, publishedPage]);

    const handleConfirmExportToDraft = useCallback(() => {
        onSave(page, activeVersion, 'export_to_draft', {
            publishedPage,
            draftPage
        });
        setShowExportToDraftDialog(false);
    }, [activeVersion, draftPage, onSave, page, publishedPage]);

    const handleWidthInputChange = (event) => {
        const next = event.target.value.replace(/[^0-9]/g, '');
        setCustomWidthInput(next);
    };

    const handleHeightInputChange = (event) => {
        const next = event.target.value.replace(/[^0-9]/g, '');
        setCustomHeightInput(next);
    };

    const handleWidthFocus = () => {
        widthInputActiveRef.current = true;
    };

    const handleWidthBlur = () => {
        widthInputActiveRef.current = false;
        setCustomWidthInput(builderWidth ? Math.round(builderWidth).toString() : '');
    };

    const handleHeightFocus = () => {
        heightInputActiveRef.current = true;
    };

    const handleHeightBlur = () => {
        heightInputActiveRef.current = false;
        setCustomHeightInput(builderHeight ? Math.round(builderHeight).toString() : '');
    };

    const handleSwapDimensions = useCallback(() => {
        if (builderWidth === null && builderHeight === null) {
            return;
        }
        const nextWidth = builderHeight ?? builderWidth ?? VIEW_PRESETS.desktop.width;
        const nextHeight = builderWidth ?? builderHeight ?? VIEW_PRESETS.desktop.height;
        const clampedWidth = clampBuilderWidth(nextWidth);
        const clampedHeight = clampBuilderHeight(nextHeight);
        const nextView = determineViewFromWidth(clampedWidth);
        if (viewRef.current !== nextView) {
            setView(nextView);
            setTimeout(() => {
                setBuilderWidth(clampedWidth);
                setBuilderHeight(clampedHeight);
                setCustomWidthInput(Math.round(clampedWidth).toString());
                setCustomHeightInput(Math.round(clampedHeight).toString());
            }, [200]);
        }

        else {
            setBuilderWidth(clampedWidth);
            setBuilderHeight(clampedHeight);
            setCustomWidthInput(Math.round(clampedWidth).toString());
            setCustomHeightInput(Math.round(clampedHeight).toString());
        }
    }, [builderHeight, builderWidth, clampBuilderHeight, clampBuilderWidth, determineViewFromWidth]);

    const handleDevicePresetSelect = useCallback((presetId) => {
        if (!presetId) {
            return;
        }

        setSelectedDevicePreset(presetId);
        setPresetDropdownOpen(false);

        if (presetId === 'custom') {
            const currentWidth = Number.isFinite(builderWidth) ? builderWidth : VIEW_PRESETS.desktop.width;
            const nudgedWidth = clampBuilderWidth(currentWidth + 1);
            const nextView = determineViewFromWidth(nudgedWidth);

            setBuilderWidth(nudgedWidth);
            setCustomWidthInput(Math.round(nudgedWidth).toString());

            if (Number.isFinite(builderHeight)) {
                setCustomHeightInput(Math.round(builderHeight).toString());
            }

            if (viewRef.current !== nextView) {
                setView(nextView);
            }
            return;
        }

        const preset = DEVICE_PRESETS.find((item) => item.id === presetId);
        if (!preset) {
            return;
        }
        const clampedWidth = clampBuilderWidth(preset.width);
        const clampedHeight = clampBuilderHeight(preset.height);

        const nextView = determineViewFromWidth(clampedWidth);
        if (viewRef.current !== nextView) {
            setView(nextView);
            setTimeout(() => {
                setBuilderWidth(clampedWidth);
                setBuilderHeight(clampedHeight);
                setCustomWidthInput(Math.round(clampedWidth).toString());
                setCustomHeightInput(Math.round(clampedHeight).toString());
            }, [200]);
        }

        else {
            setBuilderWidth(clampedWidth);
            setBuilderHeight(clampedHeight);
            setCustomWidthInput(Math.round(clampedWidth).toString());
            setCustomHeightInput(Math.round(clampedHeight).toString());
        }


    }, [builderHeight, builderWidth, clampBuilderHeight, clampBuilderWidth, determineViewFromWidth]);

    const applyCustomDimensions = useCallback(() => {
        const parsedWidth = parseInt(customWidthInput, 10);
        if (!Number.isNaN(parsedWidth)) {
            setBuilderWidth(clampBuilderWidth(parsedWidth));
        }
        const parsedHeight = parseInt(customHeightInput, 10);
        if (!Number.isNaN(parsedHeight)) {
            setBuilderHeight(clampBuilderHeight(parsedHeight));
        }
    }, [clampBuilderHeight, clampBuilderWidth, customHeightInput, customWidthInput]);

    const handleCustomInputKeyDown = (event) => {
        if (event.key === 'Enter') {
            applyCustomDimensions();
        }
    };

    const handleTogglePreviewMode = useCallback(() => {
        setIsPreviewMode((prev) => !prev);
    }, []);

    const handleToggleViewDropdown = useCallback(() => {
        setViewDropdownOpen((prev) => !prev);
    }, []);

    const handleToggleDimensionsDropdown = useCallback(() => {
        setDimensionsDropdownOpen((prev) => !prev);
    }, []);

    const handleToggleScaleDropdown = useCallback(() => {
        setScaleDropdownOpen((prev) => !prev);
    }, []);

    const handleScaleFillToggle = useCallback(() => {
        setScaleMode((prev) => (prev === 'fill' ? 'fit' : 'fill'));
        setScaleDropdownOpen(false);
    }, []);

    const handleViewSelect = useCallback((nextView) => {
        handlePresetClick(nextView);
        setViewDropdownOpen(false);
    }, [handlePresetClick]);

    const handleTogglePresetDropdown = useCallback(() => {
        setPresetDropdownOpen((prev) => !prev);
    }, []);

    const handleToggleOptionsDropdown = useCallback(() => {
        setOptionsDropdownOpen((prev) => !prev);
    }, []);

    const handleSimulatorOptionToggle = useCallback((option) => {
        if (option === 'simulator') {
            setIosSimulatorMode((prev) => !prev);
        }

        if (option === 'safari-bar') {
            setIsSafariBarOpen((prev) => !prev);
        }

        setOptionsDropdownOpen(false);
    }, []);

    const selectedDevicePresetLabel = useMemo(() => {
        if (selectedDevicePreset === 'custom') {
            return 'Custom';
        }

        return selectedDevicePresetConfig?.label ?? 'Custom';
    }, [selectedDevicePreset, selectedDevicePresetConfig]);

    const selectedViewLabel = useMemo(() => {
        return viewOptions.find((option) => option.id === view)?.label ?? 'Desktop';
    }, [view]);

    const parseCssColor = useCallback((value) => {
        if (typeof value !== 'string') {
            return null;
        }

        const color = value.trim().toLowerCase();
        if (!color || color === 'transparent') {
            return null;
        }

        const rgbaMatch = color.match(/^rgba?\(([^)]+)\)$/i);
        if (rgbaMatch) {
            const parts = rgbaMatch[1].split(',').map((item) => item.trim());
            if (parts.length < 3) {
                return null;
            }
            const red = Number(parts[0]);
            const green = Number(parts[1]);
            const blue = Number(parts[2]);
            const alpha = parts.length > 3 ? Number(parts[3]) : 1;
            if (![red, green, blue, alpha].every((component) => Number.isFinite(component))) {
                return null;
            }
            return {
                red: Math.min(Math.max(red, 0), 255),
                green: Math.min(Math.max(green, 0), 255),
                blue: Math.min(Math.max(blue, 0), 255),
                alpha: Math.min(Math.max(alpha, 0), 1)
            };
        }

        const hexMatch = color.match(/^#([\da-f]{3}|[\da-f]{6})$/i);
        if (hexMatch) {
            const hex = hexMatch[1];
            const normalized = hex.length === 3
                ? hex.split('').map((char) => `${char}${char}`).join('')
                : hex;
            const red = parseInt(normalized.slice(0, 2), 16);
            const green = parseInt(normalized.slice(2, 4), 16);
            const blue = parseInt(normalized.slice(4, 6), 16);
            return { red, green, blue, alpha: 1 };
        }

        return null;
    }, []);

    const resolveOverlayPaneColor = useCallback(() => {
        const defaultColor = 'rgba(0, 0, 0, 0.15)';
        if (typeof window === 'undefined' || !selectedBlock?.id) {
            return defaultColor;
        }

        let currentElement = document.getElementById(selectedBlock.id);
        while (currentElement) {
            const computedBackground = window.getComputedStyle(currentElement).backgroundColor;
            const parsed = parseCssColor(computedBackground);
            if (parsed && parsed.alpha > 0.01) {
                const red = parsed.red / 255;
                const green = parsed.green / 255;
                const blue = parsed.blue / 255;
                const luminance = (0.2126 * red) + (0.7152 * green) + (0.0722 * blue);
                return luminance < 0.45 ? 'rgba(255, 255, 255, 0.22)' : defaultColor;
            }
            currentElement = currentElement.parentElement;
        }

        return defaultColor;
    }, [parseCssColor, selectedBlock?.id]);

    const dimensionsLabel = useMemo(() => {
        const width = Number.isFinite(builderWidth) ? Math.round(builderWidth) : '—';
        const height = Number.isFinite(builderHeight) ? Math.round(builderHeight) : '—';
        return `${width} × ${height}`;
    }, [builderHeight, builderWidth]);

    const showDesktopBrowserFrame = view === 'desktop' && !isIosSimulatorActive;

    const renderSelectionOverlay = () => {
        if (isEyedropActive || isPreviewMode || !Array.isArray(selectionHoles) || selectionHoles.length === 0) {
            return null;
        }

        const normalizeHole = (hole) => {
            const scale = hole.scale && hole.scale > 0 ? hole.scale : 1;
            const containerWidth = (hole.viewportWidth || (containerViewportRef.current?.clientWidth ?? 0));
            const containerHeight = (hole.viewportHeight || (containerViewportRef.current?.clientHeight ?? 0));

            const rawLeft = hole.left ?? 0;
            const rawTop = hole.top ?? 0;
            const rawWidth = hole.width ?? 0;
            const rawHeight = hole.height ?? 0;

            const visibleLeft = Math.min(Math.max(rawLeft, 0), containerWidth);
            const visibleTop = Math.min(Math.max(rawTop, 0), containerHeight);
            const visibleRight = Math.min(containerWidth, Math.max(0, rawLeft + rawWidth));
            const visibleBottom = Math.min(containerHeight, Math.max(0, rawTop + rawHeight));

            return {
                id: hole.id,
                left: visibleLeft / scale,
                top: visibleTop / scale,
                width: Math.max(0, (visibleRight - visibleLeft)) / scale,
                height: Math.max(0, (visibleBottom - visibleTop)) / scale,
                right: Math.max(0, containerWidth - visibleRight) / scale,
                bottom: Math.max(0, containerHeight - visibleBottom) / scale,
            };
        };

        const normalizedHoles = selectionHoles
            .map(normalizeHole)
            .filter((hole) => hole.width > 0 && hole.height > 0);

        if (normalizedHoles.length === 0) {
            return null;
        }

        if (normalizedHoles.length > 1) {
            return (
                <div className={styles.builderOverlay} aria-hidden="true">
                    {normalizedHoles.map((hole) => (
                        <span
                            key={`selection-hole-${hole.id}`}
                            style={{
                                position: 'absolute',
                                left: hole.left,
                                top: hole.top,
                                width: hole.width,
                                height: hole.height,
                                border: '2px solid rgba(var(--secondary-rgb), 82%)',
                                background: 'rgba(var(--secondary-rgb), 10%)',
                                boxSizing: 'border-box',
                                pointerEvents: 'none',
                                zIndex: 4,
                            }}
                        />
                    ))}
                </div>
            );
        }

        const selectionHole = normalizedHoles[0];

        const overlayPaneColor = resolveOverlayPaneColor();

        return (
            <div className={styles.builderOverlay} aria-hidden="true">
                <span className={styles.overlayPane} style={{ top: 0, left: 0, width: '100%', height: selectionHole.top, background: overlayPaneColor, mixBlendMode: 'normal' }} />
                <span className={styles.overlayPane} style={{ top: selectionHole.top, left: 0, width: selectionHole.left, height: selectionHole.height, background: overlayPaneColor, mixBlendMode: 'normal' }} />
                <span className={styles.overlayPane} style={{ top: selectionHole.top, left: selectionHole.left + selectionHole.width, width: selectionHole.right, height: selectionHole.height, background: overlayPaneColor, mixBlendMode: 'normal' }} />
                <span className={styles.overlayPane} style={{ top: selectionHole.top + selectionHole.height, left: 0, width: '100%', height: selectionHole.bottom, background: overlayPaneColor, mixBlendMode: 'normal' }} />
            </div>
        );
    }

    return (
        <div className={styles.builder}>
            <div
                className={styles.builderContent}
                ref={builderContentRef}
            >
                <div className={`${styles.previewControls} ${activeVersion === 'draft' ? styles.previewControlsDraft : styles.previewControlsPublished}`}>
                    <div className={styles.devicePresetField} ref={viewDropdownRef}>
                        <button
                            type="button"
                            className={`${styles.devicePresetToggle} ${viewDropdownOpen ? styles.devicePresetToggleActive : ''}`}
                            onClick={handleToggleViewDropdown}
                            aria-haspopup="menu"
                            aria-expanded={viewDropdownOpen}
                            title="Vista"
                        >
                            {selectedViewLabel}
                        </button>
                        {viewDropdownOpen && (
                            <div className={styles.devicePresetDropdown} role="menu" aria-label="Vista">
                                {viewOptions.map((option) => (
                                    <button
                                        key={option.id}
                                        type="button"
                                        className={styles.devicePresetOption}
                                        role="menuitemradio"
                                        aria-checked={view === option.id}
                                        onClick={() => handleViewSelect(option.id)}
                                    >
                                        <span className={styles.devicePresetOptionLabel}>{option.label}</span>
                                        <span className={styles.devicePresetOptionCheck} aria-hidden="true">
                                            {view === option.id && <CheckIcon />}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className={styles.devicePresetField} ref={presetDropdownRef}>
                        <button
                            type="button"
                            className={`${styles.devicePresetToggle} ${presetDropdownOpen ? styles.devicePresetToggleActive : ''}`}
                            onClick={handleTogglePresetDropdown}
                            aria-haspopup="menu"
                            aria-expanded={presetDropdownOpen}
                            title="Seleziona un dispositivo"
                        >
                            {selectedDevicePresetLabel}
                        </button>
                        {presetDropdownOpen && (
                            <div className={styles.devicePresetDropdown} role="menu" aria-label="Seleziona un dispositivo">
                                <button
                                    type="button"
                                    className={styles.devicePresetOption}
                                    role="menuitemradio"
                                    aria-checked={selectedDevicePreset === 'custom'}
                                    onClick={() => handleDevicePresetSelect('custom')}
                                >
                                    <span className={styles.devicePresetOptionLabel}>Custom</span>
                                    <span className={styles.devicePresetOptionCheck} aria-hidden="true">
                                        {selectedDevicePreset === 'custom' && <CheckIcon />}
                                    </span>
                                </button>
                                {DEVICE_PRESETS.map((preset) => (
                                    <button
                                        key={preset.id}
                                        type="button"
                                        className={styles.devicePresetOption}
                                        role="menuitemradio"
                                        aria-checked={selectedDevicePreset === preset.id}
                                        onClick={() => handleDevicePresetSelect(preset.id)}
                                    >
                                        <span className={styles.devicePresetOptionLabel}>{preset.label}</span>
                                        <span className={styles.devicePresetOptionCheck} aria-hidden="true">
                                            {selectedDevicePreset === preset.id && <CheckIcon />}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    {selectedDevicePreset === 'custom' && (
                        <div className={`${styles.devicePresetField} ${styles.dimensionsControl}`} ref={dimensionsDropdownRef}>
                            <button
                                type="button"
                                className={`${styles.devicePresetToggle} ${dimensionsDropdownOpen ? styles.devicePresetToggleActive : ''}`}
                                onClick={handleToggleDimensionsDropdown}
                                aria-haspopup="menu"
                                aria-expanded={dimensionsDropdownOpen}
                                title="Dimensioni"
                            >
                                {dimensionsLabel}
                            </button>
                            {dimensionsDropdownOpen && (
                                <div className={`${styles.devicePresetDropdown} ${styles.customDimensionsDropdown}`} role="menu" aria-label="Dimensioni custom">
                                    <div className={styles.dimensionInputs}>
                                        <label className={styles.dimensionField}>
                                            <span>W</span>
                                            <input
                                                type="text"
                                                inputMode="numeric"
                                                value={customWidthInput}
                                                onChange={handleWidthInputChange}
                                                onFocus={handleWidthFocus}
                                                onBlur={handleWidthBlur}
                                                onKeyDown={handleCustomInputKeyDown}
                                                className={styles.dimensionInput}
                                                aria-label="Larghezza personalizzata"
                                            />
                                        </label>
                                        <SwapIcon onClick={handleSwapDimensions} className={styles.dimensionSwap} />
                                        <label className={styles.dimensionField}>
                                            <span>H</span>
                                            <input
                                                type="text"
                                                inputMode="numeric"
                                                value={customHeightInput}
                                                onChange={handleHeightInputChange}
                                                onFocus={handleHeightFocus}
                                                onBlur={handleHeightBlur}
                                                onKeyDown={handleCustomInputKeyDown}
                                                className={styles.dimensionInput}
                                                aria-label="Altezza personalizzata"
                                            />
                                        </label>
                                        <button
                                            type="button"
                                            className={styles.dimensionApply}
                                            onClick={applyCustomDimensions}
                                        >
                                            Applica
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    {isIphonePreset && (
                        <div className={styles.simulatorOptions} ref={optionsDropdownRef}>
                            <button
                                type="button"
                                className={`${styles.simulatorToggle} ${optionsDropdownOpen ? styles.simulatorToggleActive : ''}`}
                                onClick={handleToggleOptionsDropdown}
                                aria-haspopup="menu"
                                aria-expanded={optionsDropdownOpen}
                                title="Opzioni simulatore"
                            >
                                Opzioni
                            </button>
                            {optionsDropdownOpen && (
                                <div className={styles.simulatorDropdown} role="menu" aria-label="Opzioni simulatore">
                                    <button
                                        type="button"
                                        className={styles.simulatorOption}
                                        role="menuitemcheckbox"
                                        aria-checked={iosSimulatorMode}
                                        onClick={() => handleSimulatorOptionToggle('simulator')}
                                    >
                                        <span className={styles.simulatorOptionLabel}>Simulatore</span>
                                        <span className={styles.simulatorOptionCheck} aria-hidden="true">
                                            {iosSimulatorMode && <CheckIcon />}
                                        </span>
                                    </button>
                                    <button
                                        type="button"
                                        className={styles.simulatorOption}
                                        role="menuitemcheckbox"
                                        aria-checked={isSafariBarOpen}
                                        onClick={() => handleSimulatorOptionToggle('safari-bar')}
                                    >
                                        <span className={styles.simulatorOptionLabel}>Barra Indirizzi</span>
                                        <span className={styles.simulatorOptionCheck} aria-hidden="true">
                                            {isSafariBarOpen && <CheckIcon />}
                                        </span>
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                    <div className={`${styles.devicePresetField} ${styles.scaleControl}`} ref={scaleDropdownRef}>
                        <button
                            type="button"
                            className={`${styles.devicePresetToggle} ${scaleDropdownOpen ? styles.devicePresetToggleActive : ''}`}
                            onClick={handleToggleScaleDropdown}
                            aria-haspopup="menu"
                            aria-expanded={scaleDropdownOpen}
                            title="Scala"
                        >
                            {effectiveScalePercent}%
                        </button>
                        {scaleDropdownOpen && (
                            <div className={styles.devicePresetDropdown} role="menu" aria-label="Scala">
                                <button
                                    type="button"
                                    className={styles.devicePresetOption}
                                    role="menuitemcheckbox"
                                    aria-checked={scaleMode === 'fill'}
                                    onClick={handleScaleFillToggle}
                                >
                                    <span className={styles.devicePresetOptionLabel}>Riempi</span>
                                    <span className={styles.devicePresetOptionCheck} aria-hidden="true">
                                        {scaleMode === 'fill' && <CheckIcon />}
                                    </span>
                                </button>
                            </div>
                        )}
                    </div>
                </div>
                <div className={styles.viewportCenterer} ref={viewportCentererRef} onMouseDown={handleViewportBackgroundMouseDown}>
                    <div
                        ref={builderViewportRef}
                        className={styles.builderViewport}
                        style={builderViewportStyle}
                    >
                        <div className={styles.workingVersionControls}>
                            {hasDraft && (
                                <div
                                    className={`${styles.workingVersionSwitch} ${activeVersion === 'draft' ? styles.workingVersionSwitchDraft : styles.workingVersionSwitchPublished}`}
                                >
                                    <button
                                        type="button"
                                        className={`${styles.workingVersionButton} ${activeVersion === 'published' ? styles.workingVersionButtonActivePublished : ''}`}
                                        onClick={() => handleVersionRequest('published')}
                                        aria-pressed={activeVersion === 'published'}
                                    >
                                        Pubblica
                                    </button>
                                    <button
                                        type="button"
                                        className={`${styles.workingVersionButton} ${activeVersion === 'draft' ? styles.workingVersionButtonActiveDraft : ''}`}
                                        onClick={() => handleVersionRequest('draft')}
                                        aria-pressed={activeVersion === 'draft'}
                                    >
                                        Bozza
                                    </button>
                                </div>
                            )}
                            <button
                                type="button"
                                className={`${styles.previewToggleButton} ${isPreviewMode ? styles.previewToggleButtonActive : ''}`}
                                onClick={handleTogglePreviewMode}
                                aria-pressed={isPreviewMode}
                                title="Attiva o disattiva la preview interattiva"
                            >
                                {isPreviewMode && <span className={styles.previewTogglePulse} aria-hidden="true" />}
                                Preview
                            </button>
                        </div>
                        <div
                            className={`${styles.containerViewport} ${showDesktopBrowserFrame ? styles.desktopBrowserViewport : ''} ${activeVersion === 'draft' ? styles.draft : styles.public} ${isIosSimulatorActive ? styles.simulatorViewport : ''} ${isIosSimulatorActive && !hasTopCutout ? styles.simulatorNoIsland : ''} ${hasNotch ? styles.simulatorNotch : ''}`}
                            style={simulatorInsetStyle}
                            ref={containerViewportRef}
                        >
                            {showDesktopBrowserFrame && (
                                <div className={styles.desktopBrowserChrome} aria-hidden="true">
                                    <div className={styles.desktopBrowserTrafficLights}>
                                        <span className={styles.desktopBrowserDot}></span>
                                        <span className={styles.desktopBrowserDot}></span>
                                        <span className={styles.desktopBrowserDot}></span>
                                    </div>
                                    <div className={styles.desktopBrowserAddressBar}>business-school.startingfinance.com</div>
                                </div>
                            )}
                            {isIosSimulatorActive && (
                                <div className={styles.simulatorStatusBar} aria-hidden="true">
                                    <span className={styles.simulatorTime}>02:42</span>
                                    <div className={styles.simulatorIndicators}>
                                        <span className={styles.signalDots}>
                                            <i></i>
                                            <i></i>
                                            <i></i>
                                            <i></i>
                                        </span>
                                        <SimWifi style={{width: 24, height: 24}}/>
                                        <SimBattery style={{width: 34, height: 34}}/>
                                    </div>
                                </div>
                            )}
                            {isIosSimulatorActive && (
                                <div
                                    className={`${styles.simulatorSafariBar} ${isSafariBarOpen ? styles.simulatorSafariBarOpen : styles.simulatorSafariBarClosed}`}
                                    aria-hidden="true"
                                >
                                    {isSafariBarOpen ? (
                                        <div className={styles.simulatorSafariOpenPill}>
                                            <div className={styles.simulatorSafariNavGroup}>
                                                <SimChevronLeft style={{width: 24, height: 30}}/>
                                                <SimChevronRight style={{width: 24, height: 30}}/>
                                            </div>
                                            <div className={styles.simulatorSafariAddressPill}>
                                                <span className={styles.simulatorSafariAddressIcon}>
                                                    <SimReadMode style={{width: 18, height: 18}}/>
                                                </span>
                                                <span className={styles.simulatorSafariAddressText}>localhost</span>
                                                <span className={styles.simulatorSafariRefreshIcon}>
                                                    <SimReload style={{width: 22, height: 22}}/>
                                                </span>
                                            </div>
                                            <div className={styles.simulatorSafariMoreButton}>
                                                <SimEllipsis style={{width: 22, height: 22}}/>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className={styles.simulatorSafariClosedRow}>
                                            localhost
                                        </div>
                                    )}
                                </div>
                            )}
                            <div
                                ref={containerRef}
                                className={`${styles.container} ${styles[view]} ${openToolbar ? styles.collapsed : ''}`}
                                style={builderContainerStyle}
                                onMouseDownCapture={handleBuilderMouseDownCapture}
                            >
                                <Each of={page} render={(section) => {
                                    return <PageBlock
                                        element={element}
                                        view={view}
                                        grid={grid}
                                        fontScale={selectedDevicePresetConfig?.fontScale ?? 1}
                                        viewportScale={scaleFactor}
                                        previewMode={isPreviewMode}
                                        key={section.id}
                                        block={section}
                                        selectedBlock={selectedBlock}
                                        selectedBlockIds={selectedBlockIds}
                                        viewportSize={viewportSize}
                                        parentSelected={true}
                                        // onBlockAdd={(id, type) => {
                                        //     addBlock(id, type);
                                        // }}
                                        onBlockSelect={selectBlock}
                                        // onDataUpdate={(id, data) => {
                                        //     updateBlockData(id, data);
                                        // }}
                                        // onContentOrder={(id, newContent) => {
                                        //     orderBlockContent(id, newContent);
                                        // }}
                                        openBlockDialog={openBlockDialog}
                                        onInnerBlockSelect={(id, options = null) => {
                                            selectBlock(id, options)
                                        }}
                                        onRequestToolbarTab={requestToolbarTab}
                                    />
                                }} />
                                {!isPreviewMode && (
                                    <button className={styles.addSection} onClick={() => addBlock(null, BlockType.Section, page)}>
                                        + SEZIONE
                                    </button>
                                )}
                            </div>
                            {renderSelectionOverlay()}
                        </div>
                        <div
                            className={`${styles.resizeHandleHorizontal} ${isResizingHeight ? styles.resizing : ''} ${showDesktopBrowserFrame ? styles.resizeHandleInvisible : ''} ${showDesktopBrowserFrame ? styles.resizeHandleDesktopFrame : ''}`}
                            role="separator"
                            aria-orientation="horizontal"
                            aria-label="Resize builder height"
                            onPointerDown={startHeightResize}
                        />
                        <div
                            className={`${styles.resizeHandleVertical} ${isResizing ? styles.resizing : ''} ${showDesktopBrowserFrame ? styles.resizeHandleInvisible : ''}`}
                            role="separator"
                            aria-orientation="vertical"
                            aria-label="Resize builder width"
                            onPointerDown={startResize}
                        />
                        <div
                            className={`${styles.resizeHandleCorner} ${isCornerResizing ? styles.resizing : ''} ${showDesktopBrowserFrame ? styles.resizeHandleInvisible : ''}`}
                            role="separator"
                            aria-label="Resize builder width and height"
                            onPointerDown={startCornerResize}
                        />
                    </div>
                </div>

                <button
                    type="button"
                    className={styles.shortcutsFloatingButton}
                    onClick={() => setShortcutsOpen((current) => !current)}
                    aria-expanded={shortcutsOpen}
                    aria-controls="builder-shortcuts-panel"
                    aria-label="Apri elenco shortcut"
                    title="Shortcut"
                >
                    ?
                </button>

                {shortcutsOpen && (
                    <div ref={shortcutsPanelRef} id="builder-shortcuts-panel" className={styles.shortcutsFloatingPanel}>
                        <div className={styles.shortcutsFloatingHeader}>
                            <span>Shortcut</span>
                            <button
                                type="button"
                                className={styles.shortcutsFloatingClose}
                                onClick={() => setShortcutsOpen(false)}
                                aria-label="Chiudi pannello shortcut"
                            >
                                ✕
                            </button>
                        </div>
                        <div className={styles.shortcutsFloatingList}>
                            {BUILDER_SHORTCUTS.map((item) => (
                                <div key={item.keys} className={styles.shortcutsFloatingItem}>
                                    <span className={styles.shortcutsFloatingKeys}>{item.keys}</span>
                                    <span className={styles.shortcutsFloatingDescription}>{item.description}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
            <PageBuilderToolbar
                grid={grid}
                view={view}
                isPreviewMode={isPreviewMode}
                saveDisabled={customTab?.saveDisabled}
                customTab={customTab}
                onToolbarToggle={(open) => { setOpenToolbar(open) }}
                activeVersion={activeVersion}
                onPublish={handlePublishRequest}
                onSaveDraft={handleSaveDraftRequest}
                onExportToDraft={handleExportToDraftRequest}
                isSaving={isSaving}
                lastSavedAt={lastSavedAt}
                publishButtonStatus={publishButtonStatus}
                saveDraftButtonStatus={saveDraftButtonStatus}
                hideExportToDraft={hideExportToDraft}
                onGridUpdate={(show) => {
                    setGrid(show)
                }}
                forcedTab={forcedToolbarTab}
                onForcedTabApplied={handleForcedTabApplied}
                onEyedropStateChange={handleEyedropStateChange}
            />
            {selectedBlock && renderBlockDialog(selectedBlock.type)}
            <AlertDialog
                appearance="modern"
                open={showVersionSwitchDialog}
                title={pendingVersion === 'draft' ? "Passare alla bozza?" : "Passare alla versione pubblica?"}
                text="Se cambi modalità ora, perderai tutte le modifiche non salvate della modalità corrente."
                status={DialogStatus.Default}
                actions={[
                    {
                        label: 'ANNULLA',
                        inverse: true,
                        onClick: async () => {
                            setShowVersionSwitchDialog(false);
                            setPendingVersion(null);
                        }
                    },
                    {
                        label: pendingVersion === 'draft' ? 'Passa alla bozza' : 'Passa alla versione pubblica',
                        accentColor: 'var(--sf-red)',
                        onClick: async () => {
                            applyVersionChange();
                        }
                    }
                ]}
                onClose={() => {
                    setShowVersionSwitchDialog(false);
                    setPendingVersion(null);
                }}
            />
            <AlertDialog
                appearance="modern"
                open={showPublishDraftDialog}
                title="Pubblicare la bozza?"
                text="Confermando, il contenuto pubblico verrà sostituito con la bozza corrente."
                status={DialogStatus.Default}
                actions={[
                    {
                        label: 'ANNULLA',
                        inverse: true,
                        onClick: async () => {
                            setShowPublishDraftDialog(false);
                        }
                    },
                    {
                        label: 'PUBBLICA ORA',
                        onClick: async () => {
                            handleConfirmPublishDraft();
                        }
                    }
                ]}
                onClose={() => {
                    setShowPublishDraftDialog(false);
                }}
            />
            <AlertDialog
                appearance="modern"
                open={showExportToDraftDialog}
                title="Esportare in bozza?"
                text="Confermando, la bozza verrà sostituita con il contenuto pubblico corrente."
                status={DialogStatus.Default}
                actions={[
                    {
                        label: 'ANNULLA',
                        inverse: true,
                        onClick: async () => {
                            setShowExportToDraftDialog(false);
                        }
                    },
                    {
                        label: 'ESPORTA ORA',
                        onClick: async () => {
                            handleConfirmExportToDraft();
                        }
                    }
                ]}
                onClose={() => {
                    setShowExportToDraftDialog(false);
                }}
            />
        </div>
    );
};

export default PageBuilder;
